import type { MeetingState } from '@wundo/ui';
import type { CalendarMeetingItem } from '@/lib/meetings.ts';
import { meetingTitle } from './meetingFormat.ts';
import { m } from '@/paraglide/messages.js';

/**
 * What a card on the agenda is, which is also what a click on it does.
 *
 * In Wundo: open it. On the calendar only: add it. And a task — an event with
 * nobody else on it — is neither: there is no counterpart, so there is no
 * conversation for Wundo to help with and nothing for the click to do. Nor is
 * busy time the organisation's agendaproxy blocked: Wundo only knows when it is.
 */
export function meetingState(meeting: CalendarMeetingItem): MeetingState {
  if (meeting.blocked) return 'inert';
  if (meeting.tracked) return 'tracked';
  return meeting.bucket === 'task' ? 'inert' : 'candidate';
}

/**
 * The journey step Wundo suggests for a meeting it already follows and that is
 * on no journey yet. That card's click opens the meeting, so the suggestion
 * needs a control of its own.
 */
export function followedJourneySuggestion(
  meeting: CalendarMeetingItem,
): CalendarMeetingItem['journey_suggestion'] {
  if (meetingState(meeting) !== 'tracked' || meeting.journey) return null;
  return meeting.journey_suggestion;
}

/**
 * What the card is called to someone who cannot see it. The title alone would
 * leave the two actions indistinguishable, and they are the whole difference
 * between the two kinds of card.
 */
export function meetingCardLabel(meeting: CalendarMeetingItem): string | undefined {
  const title = meetingTitle(meeting);

  switch (meetingState(meeting)) {
    case 'tracked':
      return m.agenda_card_open({ title });
    case 'candidate':
      return meeting.journey_suggestion
        ? m.agenda_card_link({
            title,
            position: meeting.journey_suggestion.step_position,
            name: meeting.journey_suggestion.type_name,
          })
        : m.agenda_card_add({ title });
    /* Nothing to announce: it is not a control. */
    case 'inert':
      return undefined;
  }
}
